import {
  DateRange,
  PREDEFINED_DATE_RANGES,
  PredefinedDate,
} from '@/components/datePicker/types/datePicker';
import { UTCDate } from '@date-fns/utc';
import { subYears } from 'date-fns';
import { endOfDay } from 'date-fns/endOfDay';
import { endOfMonth } from 'date-fns/endOfMonth';
import { endOfYear } from 'date-fns/endOfYear';
import { format } from 'date-fns/format';
import { isEqual as isEqualDateFns } from 'date-fns/isEqual';
import { isValid } from 'date-fns/isValid';
import { de } from 'date-fns/locale/de';
import { enGB } from 'date-fns/locale/en-GB';
import { es } from 'date-fns/locale/es';
import { fi } from 'date-fns/locale/fi';
import { nb } from 'date-fns/locale/nb';
import { sv } from 'date-fns/locale/sv';
import { parse } from 'date-fns/parse';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfYear } from 'date-fns/startOfYear';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { includes, isEqual as isEqualLodash } from 'lodash-es';

export function toUTCDate(value: Date): UTCDate;
export function toUTCDate(value: Date | string | null | undefined): UTCDate | null;
export function toUTCDate(value: Date | string | null | undefined): UTCDate | null {
  if (!value || !isValid(new Date(value))) return null;
  const date = typeof value === 'string' ? new Date(value) : value;
  return new UTCDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
}

const localNow = new Date();
const localNowToUTCDate = toUTCDate(localNow);
if (!localNowToUTCDate) throw new Error('Critical error. `Now` is null');
export const nowAsUTC = localNowToUTCDate;

export const getUTCDateRangeFromPredefined = (predefinedDate?: PredefinedDate) => {
  const defaultCurrentYear = { from: startOfYear(new UTCDate()), to: endOfYear(new UTCDate()) };
  if (!predefinedDate) return defaultCurrentYear;

  const dateRanges: Record<PredefinedDate, DateRange> = {
    [PREDEFINED_DATE_RANGES.YESTERDAY]: {
      from: startOfDay(subDays(nowAsUTC, 1)),
      to: endOfDay(subDays(nowAsUTC, 1)),
    },
    [PREDEFINED_DATE_RANGES.LAST_7_DAYS]: {
      from: startOfDay(subDays(nowAsUTC, 6)),
      to: endOfDay(nowAsUTC),
    },
    [PREDEFINED_DATE_RANGES.LAST_30_DAYS]: {
      from: startOfDay(subDays(nowAsUTC, 29)),
      to: endOfDay(nowAsUTC),
    },
    [PREDEFINED_DATE_RANGES.PREVIOUS_MONTH]: {
      from: startOfDay(startOfMonth(subMonths(nowAsUTC, 1))),
      to: endOfDay(endOfMonth(subMonths(nowAsUTC, 1))),
    },
    [PREDEFINED_DATE_RANGES.PREVIOUS_THREE_MONTHS]: {
      from: startOfMonth(subMonths(nowAsUTC, 3)),
      to: endOfMonth(subMonths(nowAsUTC, 1)),
    },
    [PREDEFINED_DATE_RANGES.PREVIOUS_YEAR]: {
      from: startOfYear(subYears(nowAsUTC, 1)),
      to: endOfYear(subYears(nowAsUTC, 1)),
    },
    [PREDEFINED_DATE_RANGES.LAST_3_MONTHS]: {
      from: startOfDay(startOfMonth(subMonths(nowAsUTC, 2))),
      to: endOfDay(nowAsUTC),
    },
    [PREDEFINED_DATE_RANGES.LAST_12_MONTHS]: {
      from: startOfDay(startOfMonth(subMonths(nowAsUTC, 11))),
      to: endOfDay(nowAsUTC),
    },
    [PREDEFINED_DATE_RANGES.CURRENT_YEAR]: defaultCurrentYear,
  };

  const range = dateRanges[predefinedDate] || defaultCurrentYear;
  return range;
};

export const getPredefinedFromUTCDateRange = (
  dateRange: DateRange | PredefinedDate | undefined,
  validPredefinedDates: PredefinedDate[] | undefined,
) => {
  if (isPredefinedDate(dateRange, validPredefinedDates)) return dateRange;
  if (!dateRange || !dateRange.from || !dateRange.to || !validPredefinedDates) return undefined;
  for (const validPredefined of validPredefinedDates) {
    const predefinedAsUTC = getUTCDateRangeFromPredefined(validPredefined);
    if (isEqualLodash(dateRange, predefinedAsUTC)) return validPredefined;
  }
  return undefined;
};

export const isPredefinedDate = (
  range: PredefinedDate | DateRange | undefined,
  validPredefinedRanges: PredefinedDate[] | undefined,
): range is PredefinedDate => {
  return (
    typeof range === 'string' && !!validPredefinedRanges && validPredefinedRanges.includes(range)
  );
};

export const isDateRangeWithNoPredefinedEquivalent = (
  range: PredefinedDate | DateRange | undefined,
  validPredefinedRanges: PredefinedDate[] | undefined,
): range is DateRange | undefined => {
  const predefinedEquivalent = getPredefinedFromUTCDateRange(range, validPredefinedRanges);
  if (!predefinedEquivalent || !validPredefinedRanges) return true;
  return !includes(validPredefinedRanges, predefinedEquivalent);
};

export const isMonthRange = (range: DateRange | undefined) => {
  if (!range) return false;
  const start = startOfMonth(range?.from);
  const end = endOfMonth(range.to);
  return isEqualDateFns(range.from, start) && isEqualDateFns(range.to, end);
};

export const getMonthRangeFromPredefined = (
  predefined: PredefinedDate | undefined,
  validPredefinedRanges: PredefinedDate[] | undefined,
) => {
  if (!includes(validPredefinedRanges, predefined)) return undefined;
  const dateRange = getUTCDateRangeFromPredefined(predefined);
  return isMonthRange(dateRange) ? dateRange : undefined;
};

export const asUTCStartOfDayToEndOfDayRange = (dateRange: DateRange) => {
  const { from, to } = dateRange;
  const fromUTC = toUTCDate(from);
  const toUTC = toUTCDate(to);
  return { from: startOfDay(fromUTC), to: endOfDay(toUTC) };
};

export const isValidDateInput = (dateString: string, format: shortDateFormat): boolean => {
  const regexMap: Record<shortDateFormat, RegExp> = {
    'dd.MM.yy': /^\d{2}\.\d{2}\.\d{2}$/,
    'yyyy-MM-dd': /^\d{4}-\d{2}-\d{2}$/,
  };
  const exp = regexMap[format].test(dateString) ?? false;

  const parsedDate = isValid(parse(dateString, format, new UTCDate()));
  return exp && parsedDate;
};

export const formatToShortDateStringInUTC = (date: Date, shortDateFormat: shortDateFormat) => {
  const utcDate = toUTCDate(date);
  return format(utcDate, shortDateFormat);
};

// Localization of dates for react-day-picker using date-fns locale

export const dateLocaleMap = { no: nb, en: enGB, sv: sv, fi, de, es } as const;

export const getDateLocale = (locale: keyof typeof dateLocaleMap) => {
  return dateLocaleMap[locale] ?? enGB;
};

export const SHORT_DATE_FORMATS = ['dd.MM.yy', 'yyyy-MM-dd'] as const;

export type shortDateFormat = (typeof SHORT_DATE_FORMATS)[number];
